import {
  ApiClient,
  type ApiClientOptions,
  type CursorPagedResponse,
} from '@/infra/api-client'
import type {
  ListWorkspacePostingInvitationsRequest,
  ListWorkspacePostingOpportunitiesRequest,
  WorkspaceInvitationResource,
  WorkspacePostingOpportunityResource,
} from '@/features/opportunities/api/resources/workspace-posting-opportunity'
import { toURLSearchParams } from '@/utils/url-util'

/**
 * Workspace postings, from the applicant's side.
 */
export class WorkspacePostingsApi {
  readonly opportunities
  readonly invitations

  constructor(options: ApiClientOptions) {
    this.opportunities = new WorkspacePostingOpportunityApi(options)
    this.invitations = new WorkspacePostingInvitationApi(options)
  }
}

/**
 * API for workspace postings open to the current user.
 */
export class WorkspacePostingOpportunityApi extends ApiClient {
  list(
    request: ListWorkspacePostingOpportunitiesRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<WorkspacePostingOpportunityResource>> {
    return this._get(
      `v3/postings/opportunities`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  apply(id: string): Promise<void> {
    return this._patch(`v3/postings/${id}/apply`, undefined)
  }

  withdraw(id: string): Promise<void> {
    return this._patch(`v3/postings/${id}/withdraw`, undefined)
  }
}

/**
 * API for workspace posting invitations to the current user.
 */
export class WorkspacePostingInvitationApi extends ApiClient {
  list(
    request: ListWorkspacePostingInvitationsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<WorkspaceInvitationResource>> {
    return this._get(
      `v3/postings/invitations`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  accept(id: string): Promise<void> {
    return this._patch(`v3/postings/${id}/accept`, undefined)
  }

  reject(id: string): Promise<void> {
    return this._patch(`v3/postings/${id}/reject`, undefined)
  }
}
